import React, { useRef, useEffect, useState, FC } from "react";

import { Icon, Heading, Button, TextField } from "@telia/teddy";
import { SMS_VERIFICATION_CODE_LEN, VerificationTarget } from "../../../../../../../constants";
import { VerificationDeliveryMethod, VERIFICATION_DELIVERY_METHOD } from "../../../../consts";
import TEXT from "../../../../text-consts";
import "./VerificationBlock.less";

interface VerificationBlockProps {
    children?: React.ReactNode;
    id: string;
    isSendingSMSOrEmail: boolean;
    method: VerificationDeliveryMethod;
    onCodeConfirm: (code: string, target: VerificationTarget) => void;
    onCodeSend: (target: VerificationTarget, method: VerificationDeliveryMethod) => void;
    target: VerificationTarget;
    verified: boolean;
}

const VerificationBlock: FC<VerificationBlockProps> = ({
    id,
    verified,
    target,
    method,
    onCodeSend,
    children,
    isSendingSMSOrEmail,
    onCodeConfirm,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [code, setCode] = useState("");

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [isSendingSMSOrEmail]);

    let labelText;
    if (method === VERIFICATION_DELIVERY_METHOD.EMAIL) {
        labelText = TEXT.PERSONAL_DETAILS.VERIFICATION.WITH_EMAIL_LABEL;
    } else if (method === VERIFICATION_DELIVERY_METHOD.SMS) {
        labelText = TEXT.PERSONAL_DETAILS.VERIFICATION.WITH_SMS_LABEL;
    }

    return (
        <div id={id} className="verification-block">
            {children}
            {!verified ? (
                <div className="verification-block__buttons verification-block__buttons--bottom-aligned">
                    {!isSendingSMSOrEmail && (
                        <>
                            <div className="verification-block__sending-container">
                                <Heading mb="100" as="h2" variant="title-100">
                                    {TEXT.PERSONAL_DETAILS.VERIFICATION.CONFIRM_HEADING}
                                </Heading>
                            </div>
                            <div className="verification-block__form-container">
                                <label
                                    htmlFor="verification-block-code-input"
                                    className="verification-block__type-code-label telia-textfield__label"
                                >
                                    {labelText}
                                </label>
                                <TextField className="verification-block__type-code">
                                    <TextField.Input
                                        ref={inputRef}
                                        type="text"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        maxLength={4}
                                        data-di-mask
                                    />
                                </TextField>
                                <Button
                                    className="verification-block__confirm-button"
                                    variant="secondary"
                                    size="sm"
                                    disabled={code.length !== SMS_VERIFICATION_CODE_LEN}
                                    onClick={() => onCodeConfirm(code, target)}
                                >
                                    Bekreft kode
                                </Button>
                            </div>
                            <div className="verification-block__resend-code-button">
                                <Button
                                    variant="text-underline"
                                    onClick={() => {
                                        onCodeSend(target, method);
                                        setCode("");
                                    }}
                                >
                                    Send koden på nytt
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="verification-block__success">
                    <Icon name="check-circle" className="verification-block__icon" />
                    <span>{TEXT.PERSONAL_DETAILS.VERIFICATION.CODE_VERIFIED}</span>
                </div>
            )}
        </div>
    );
};

export default VerificationBlock;
